/**
 * Phase labels and grid styling (AG Grid) + project detail page shell accents.
 */
import { isCvinExpansion, phaseLabelForActiveSubphase, summaryGroup, ACF, currentSubphaseSlugForRow } from "./projectGridColumns";
import {
  isRenewalSubtype,
  isNonRevenueOrCapitalProject,
  seedEnteredPhaseDefaultSubphase,
} from "./plugins/commercialSubphases";
import {
  EXPANSION_ACF_GROUPS,
  readSubsegmentSummaryFromAcf,
  syncSubsegmentSummaryToLegacy,
} from "./plugins/expansionProjectAcf";

export const GLOBAL_PHASES = [
  "Sales",
  "Finance",
  "Drafting",
  "Permitting",
  "Construction",
  "Fiber",
  "Networking",
  "Accounting",
];

export const PHASE_NAME_BY_ID = GLOBAL_PHASES.reduce((acc, name, index) => {
  acc[index + 1] = name;
  return acc;
}, {});

export const PHASE_SLUG_TO_LABEL = {
  sales: "Sales",
  finance: "Finance",
  drafting: "Drafting",
  engineering: "Drafting",
  permitting: "Permitting",
  construction: "Construction",
  fiber: "Fiber",
  networking: "Networking",
  accounting: "Accounting",
};

/** Background for Phase column cells (labels match displayPhaseForRow / GLOBAL_PHASES). */
export const PHASE_CELL_BACKGROUND = {
  Sales: "#99d128",
  Accounting: "#4f8a43",
  Engineering: "#a32f2f",
  Drafting: "#a32f2f",
  Engineering: "#a32f2f",
  Construction: "#c2905f",
  Fiber: "#d1af4f",
  Permitting: "#ffa500",
  Networking: "#3474eb",
  Finance: "#9574a1",
};

/** CSS class per phase (used with App.css + !important so theme/cache cannot force green). */
export const PHASE_CELL_CLASS = {
  Sales: "phase-cell-sales",
  Accounting: "phase-cell-accounting",
  Engineering: "phase-cell-engineering",
  Drafting: "phase-cell-engineering",
  Construction: "phase-cell-construction",
  Fiber: "phase-cell-fiber",
  Permitting: "phase-cell-permitting",
  Networking: "phase-cell-networking",
  Finance: "phase-cell-finance",
};

/** Modifier class on .project-detail-shell; sets --pd-phase-bar / --pd-phase-fill in App.css */
export const PHASE_DETAIL_SECTION_CLASS = {
  Sales: "phase-section-sales",
  Accounting: "phase-section-accounting",
  Engineering: "phase-section-engineering",
  Drafting: "phase-section-engineering",
  Construction: "phase-section-construction",
  Fiber: "phase-section-fiber",
  Permitting: "phase-section-permitting",
  Networking: "phase-section-networking",
  Finance: "phase-section-finance",
};

export function resolvePhaseStyleKey(label) {
  if (label == null || label === "") return null;
  if (PHASE_CELL_BACKGROUND[label]) return label;
  const lower = String(label).toLowerCase();
  return Object.keys(PHASE_CELL_BACKGROUND).find((k) => k.toLowerCase() === lower) ?? null;
}

/** Blend hex color toward white (whiteFraction 0.2 ≈ 20% lighter / whiter). */
export function mixHexWithWhite(hex, whiteFraction) {
  const h = String(hex).replace(/^#/, "").trim();
  if (h.length !== 6 || Number.isNaN(whiteFraction)) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const w = Math.min(1, Math.max(0, whiteFraction));
  const nr = Math.round(r * (1 - w) + 255 * w);
  const ng = Math.round(g * (1 - w) + 255 * w);
  const nb = Math.round(b * (1 - w) + 255 * w);
  return `rgb(${nr}, ${ng}, ${nb})`;
}

/** Black or white label text for a phase background (grid Phase column). */
export function contrastTextForPhaseBackground(hex) {
  const h = String(hex ?? "").replace(/^#/, "").trim();
  if (h.length !== 6) return "#ffffff";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if (![r, g, b].every(Number.isFinite)) return "#ffffff";
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 140 ? "#000000" : "#ffffff";
}

export const PHASE_CELL_FOREGROUND = Object.fromEntries(
  Object.entries(PHASE_CELL_BACKGROUND).map(([phase, bg]) => [
    phase,
    contrastTextForPhaseBackground(bg),
  ])
);

export function displayPhaseForRow(row) {
  if (!row) return "";
  const summary = summaryGroup(row);
  if (summary && typeof summary === "object") {
    if (isCvinExpansion(row)) {
      const subSummary = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
      const phase =
        subSummary?.phase ??
        summary.phase_exp ??
        summary.phase;
      if (phase != null && String(phase).trim() !== "") return String(phase).trim();
    } else {
      const ph = summary.phase;
      if (ph != null && String(ph).trim() !== "") return String(ph).trim();
    }
  }
  if (row.phase_label != null && String(row.phase_label).trim() !== "") {
    return String(row.phase_label).trim();
  }
  const phaseId = row.phase ?? row.current_phase_id;
  if (phaseId != null && phaseId !== "") {
    const n = Number(phaseId);
    if (Number.isFinite(n) && PHASE_NAME_BY_ID[n]) return PHASE_NAME_BY_ID[n];
    return String(phaseId);
  }
  const slug = row.phase_slug;
  if (slug == null || String(slug).trim() === "") return "";
  const key = String(slug).trim().toLowerCase().replace(/\s+/g, "_");
  return PHASE_SLUG_TO_LABEL[key] || String(slug).trim();
}

export function phaseCellClass(params) {
  if (isPermittingPendingRevisionsRow(params.data)) {
    return "phase-cell-permitting-pending-revisions";
  }
  const key = resolvePhaseStyleKey(displayPhaseForRow(params.data));
  return key ? PHASE_CELL_CLASS[key] : "phase-cell-default";
}

export function phaseCellStyle(params) {
  if (isPermittingPendingRevisionsRow(params.data)) {
    return {
      background: "linear-gradient(90deg, #ffa500 50%, #a32f2f 50%)",
      backgroundColor: "#ffa500",
      color: "#ffffff",
      fontWeight: 400,
      textAlign: "center",
    };
  }
  const key = resolvePhaseStyleKey(displayPhaseForRow(params.data));
  const bg = key ? PHASE_CELL_BACKGROUND[key] : null;
  if (!bg) return null;
  return {
    backgroundColor: bg,
    color: contrastTextForPhaseBackground(bg),
    fontWeight: 400,
    textAlign: "center",
  };
}

/** True when phase is Permitting and subphase is Pending Revisions (split Drafting/Permitting colors). */
export function isPermittingPendingRevisionsRow(row) {
  const phase = displayPhaseForRow(row);
  if (String(phase || "").trim().toLowerCase() !== "permitting") return false;
  return currentSubphaseSlugForRow(row) === "pending_revisions";
}

export function subphaseCellStyle(params) {
  const label = phaseLabelForActiveSubphase(params.data);
  const key = resolvePhaseStyleKey(label);
  const bg = key ? PHASE_CELL_BACKGROUND[key] : null;
  if (!bg) return null;
  return {
    backgroundColor: mixHexWithWhite(bg, 0.3),
    color: "#0f172a",
    fontWeight: 400,
    textAlign: "center",
  };
}

export function getPhaseDetailShellClassName(row) {
  const key = resolvePhaseStyleKey(displayPhaseForRow(row));
  const mod = key ? PHASE_DETAIL_SECTION_CLASS[key] : null;
  return `project-detail-shell ${mod || "phase-section-default"}`;
}

const ACF_SUMMARY_SNAKE = "project_summary_test_2";

/** Commercial (cvin_expansion !== true). */
export const COMMERCIAL_PHASE_FLOW = [
  "Sales",
  "Finance",
  "Drafting",
  "Permitting",
  "Construction",
  "Fiber",
  "Networking",
  "Accounting",
];

/** Narrowed commercial flows from Project Summary `department_involvement` (see commercialPhaseFlowFromDepartmentInvolvement). */
const COMMERCIAL_FLOW_FINANCE_TO_FIBER = [
  "Sales",
  "Finance",
  "Fiber",
  "Networking",
  "Accounting",
];
const COMMERCIAL_FLOW_FINANCE_TO_NETWORKING = ["Sales", "Finance", "Networking", "Accounting"];
const COMMERCIAL_FLOW_FINANCE_TO_ACCOUNTING = ["Sales", "Finance", "Accounting"];
const COMMERCIAL_FLOW_SALES_TO_ACCOUNTING = ["Sales", "Accounting"];

/**
 * Maps `department_involvement` strings to phase-skip flags. Unknown labels are ignored.
 * "Splicing" counts as fiber (matches ACF export data).
 * @param {unknown} involvement
 * @returns {Set<string>} one or more of: eng, const, fiber, net, acct
 */
export function commercialDeptInvolvementTokenSet(involvement) {
  const out = new Set();
  if (!Array.isArray(involvement)) return out;
  for (const raw of involvement) {
    const t = String(raw ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    if (!t) continue;
    if (t === "engineering" || t === "drafting") out.add("eng");
    else if (t === "construction") out.add("const");
    else if (t === "fiber" || t === "splicing") out.add("fiber");
    else if (t === "networking") out.add("net");
    else if (t === "accounting") out.add("acct");
  }
  return out;
}

/** Only Accounting department selected (no Engineering, Construction, Fiber, or Networking). */
export function isAccountingOnlyDepartments(involvement) {
  if (!Array.isArray(involvement) || involvement.length === 0) return false;
  const rel = commercialDeptInvolvementTokenSet(involvement);
  if (rel.size === 0) return false;
  return (
    rel.has("acct") && !rel.has("eng") && !rel.has("const") && !rel.has("fiber") && !rel.has("net")
  );
}

/** Networking selected; Engineering / Construction / Fiber not (Accounting optional). */
export function isNetworkingAccountingOnlyDepartments(involvement) {
  if (!Array.isArray(involvement) || involvement.length === 0) return false;
  const rel = commercialDeptInvolvementTokenSet(involvement);
  if (!rel.has("net")) return false;
  return !rel.has("eng") && !rel.has("const") && !rel.has("fiber");
}

/** All commercial departments: Engineering, Construction, Fiber, Networking, Accounting. */
export function isAllDepartmentsInvolvement(involvement) {
  if (!Array.isArray(involvement) || involvement.length === 0) return false;
  const rel = commercialDeptInvolvementTokenSet(involvement);
  return (
    rel.has("eng") && rel.has("const") && rel.has("fiber") && rel.has("net") && rel.has("acct")
  );
}

/**
 * When `department_involvement` is non-empty, commercial projects may skip phases:
 * only Accounting → Finance → Accounting; no eng/construction/fiber → Finance → Networking;
 * no eng/construction → Finance → Fiber; otherwise full {@link COMMERCIAL_PHASE_FLOW}.
 * Empty or missing involvement keeps the full flow (legacy / unset).
 */
export function commercialPhaseFlowFromDepartmentInvolvement(involvement, { isRenewal = false } = {}) {
  if (!Array.isArray(involvement) || involvement.length === 0) {
    return COMMERCIAL_PHASE_FLOW;
  }
  const rel = commercialDeptInvolvementTokenSet(involvement);
  if (rel.size === 0) {
    return COMMERCIAL_PHASE_FLOW;
  }
  const eng = rel.has("eng");
  const cons = rel.has("const");
  const fiber = rel.has("fiber");
  const net = rel.has("net");
  const acct = rel.has("acct");
  if (acct && !eng && !cons && !fiber && !net) {
    if (isRenewal) return COMMERCIAL_FLOW_SALES_TO_ACCOUNTING;
    return COMMERCIAL_FLOW_FINANCE_TO_ACCOUNTING;
  }
  if (!eng && !cons && !fiber) {
    return COMMERCIAL_FLOW_FINANCE_TO_NETWORKING;
  }
  if (!eng && !cons) {
    return COMMERCIAL_FLOW_FINANCE_TO_FIBER;
  }
  return COMMERCIAL_PHASE_FLOW;
}

/** Commercial phase flow from project summary (renewal + department involvement). */
export function commercialPhaseFlowForSummary(summary) {
  const involvement = summary?.departments ?? summary?.department_involvement;
  let flow = commercialPhaseFlowFromDepartmentInvolvement(involvement, {
    isRenewal: isRenewalSubtype(summary),
  });
  if (isNonRevenueOrCapitalProject(summary)) {
    flow = flow.filter((phase) => phase !== "Sales" && phase !== "Finance");
  }
  return flow.length ? flow : ["Accounting"];
}

/** Expansion (cvin_expansion === true). */
export const EXPANSION_PHASE_FLOW = [
  "Drafting",
  "Permitting",
  "Construction",
  "Fiber",
  "Accounting",
];

/** Normalize for ordering ("Engineering" and "Drafting" share the drafting step). */
export function canonicalPhaseKey(label) {
  const t = String(label ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (t === "engineering" || t === "drafting") return "drafting";
  return t;
}

export function phaseFlowForRow(row) {
  if (isCvinExpansion(row)) return EXPANSION_PHASE_FLOW;
  const summary = summaryGroup(row);
  return commercialPhaseFlowForSummary(summary);
}

export function flowIndexForPhase(flow, currentLabel) {
  const k = canonicalPhaseKey(currentLabel);
  if (!k) return -1;
  return flow.findIndex((p) => canonicalPhaseKey(p) === k);
}

/** Next phase label, or null if unknown or already at end. */
export function nextPhaseLabelForRow(row, currentDisplayLabel) {
  const flow = phaseFlowForRow(row);
  const cur = String(currentDisplayLabel ?? "").trim();
  if (!cur) return null;
  const idx = flowIndexForPhase(flow, cur);
  if (idx < 0) return null;
  if (idx >= flow.length - 1) return null;
  return flow[idx + 1];
}

const ACF_SUMMARY_LEGACY_LABEL = "Project Summary Test 2";

function resolveSummaryKeyForWrite(clone) {
  const hasMain =
    clone[ACF.SUMMARY] && typeof clone[ACF.SUMMARY] === "object" && !Array.isArray(clone[ACF.SUMMARY]);
  const hasLegacyLabel =
    clone[ACF_SUMMARY_LEGACY_LABEL] &&
    typeof clone[ACF_SUMMARY_LEGACY_LABEL] === "object" &&
    !Array.isArray(clone[ACF_SUMMARY_LEGACY_LABEL]);
  const hasSnake =
    clone[ACF_SUMMARY_SNAKE] &&
    typeof clone[ACF_SUMMARY_SNAKE] === "object" &&
    !Array.isArray(clone[ACF_SUMMARY_SNAKE]);
  if (hasMain) return ACF.SUMMARY;
  if (hasLegacyLabel) return ACF_SUMMARY_LEGACY_LABEL;
  if (hasSnake) return ACF_SUMMARY_SNAKE;
  return ACF.SUMMARY;
}

/** Legacy `current_phase_id` (Drafting uses the Engineering slot). */
export function phaseLabelToCurrentPhaseId(label) {
  const t = String(label ?? "")
    .trim()
    .toLowerCase();
  if (t === "drafting") return 3;
  const idx = GLOBAL_PHASES.findIndex((n) => n.toLowerCase() === t);
  return idx >= 0 ? idx + 1 : null;
}

/**
 * PATCH body: Project Summary `phase` / `phase_exp`, `phase_label`, optional `current_phase_id`.
 * Also seeds the next phase’s first/default subphase (e.g. Construction → Ready to schedule).
 */
export function buildAdvancePhasePatch(row, nextLabel) {
  const ag = row?.acf_groups_json;
  const clone =
    ag != null && typeof ag === "object" && !Array.isArray(ag) ? JSON.parse(JSON.stringify(ag)) : {};
  const summaryKey = resolveSummaryKeyForWrite(clone);
  if (!clone[summaryKey] || typeof clone[summaryKey] !== "object" || Array.isArray(clone[summaryKey])) {
    clone[summaryKey] = {};
  }
  const summary = clone[summaryKey];
  const expansion = isCvinExpansion(row);
  if (expansion) {
    const subKey = EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY;
    if (!clone[subKey] || typeof clone[subKey] !== "object" || Array.isArray(clone[subKey])) {
      clone[subKey] = {};
    }
    const existingSub = clone[subKey];
    const seeded = seedEnteredPhaseDefaultSubphase(
      {
        ...existingSub,
        phase: nextLabel,
        cvin_expansion: true,
        subphases:
          existingSub.subphases ||
          summary.subphases_exp ||
          summary.subphases ||
          existingSub.subphases_exp,
      },
      nextLabel
    );
    clone[subKey] = {
      ...existingSub,
      ...seeded,
      phase: nextLabel,
      cvin_expansion: true,
    };
    // Mirror into project_summary / legacy expansion groups for grid + DB columns.
    const synced = syncSubsegmentSummaryToLegacy({
      ...clone,
      [EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY]: clone[subKey],
      [EXPANSION_ACF_GROUPS.SUMMARY]: summary,
    });
    Object.assign(clone, synced);
  } else {
    clone[summaryKey] = seedEnteredPhaseDefaultSubphase(summary, nextLabel);
  }
  const out = {
    acf_groups_json: clone,
    phase_label: nextLabel,
    _source: "landing-phase-complete",
  };
  if (expansion) out.cvin_expansion = true;
  return out;
}
